import { h, Fragment } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { Tab } from '@headlessui/react';
import clsx from 'clsx';
import ClickSelection from '../../threekit-components/click-selection';
import { useThreekit } from '../../threekit-context';
import Image from '../../../utils/image';
import { formatMoney } from '../../../utils/helpers';
import { useModal } from '../../modal-context';
import CompareMaterialsModal from '../modals/compare-materials-modal';
import { Swiper, SwiperSlide } from '../swiper';
import ThreekitIcons from '../threekit-icons';

function HandleDrawerColors() {
  const {
    bladeSide,
    colorAndMaterialOrder,
    getProductsFromAttribute,
    getSelectedVariant,
    darkMode,
    backgroundColor,
    getBackgroundColorFromSelectedVariant,
    category,
    setCategory,
    bladeColorDuplicateSide,
    setBladeColorDuplicateSide,
  } = useThreekit();
  const { modalName, openModal } = useModal();
  const buttonContainerRef = useRef(null);
  const variantBackgroundColor = getBackgroundColorFromSelectedVariant();
  const materialProducts = getProductsFromAttribute('Handle Material');
  const sideColorProducts = getProductsFromAttribute(
    `${bladeSide} Handle Color`,
  );
  const colorProducts =
    sideColorProducts.length > 0
      ? sideColorProducts
      : getProductsFromAttribute(`Handle Color`);
  let selectedVariant = getSelectedVariant(colorProducts);

  // Check if the `${bladeSide} Handle Color` property exists
  // in the selectedVariant.configuration object
  if (
    !selectedVariant ||
    !selectedVariant.configuration ||
    !selectedVariant.configuration[`${bladeSide} Handle Color`]
  ) {
    // If not, try to get a new selectedVariant with the getSelectedVariant() function
    selectedVariant = getSelectedVariant(colorProducts);

    // If it still doesn't exist, that means that there is no color config.
    // so run through all material products to find the right one.
    if (!selectedVariant) {
      selectedVariant = getSelectedVariant(materialProducts);
    }
  }

  const groupedMaterials = materialProducts.reduce((acc, obj) => {
    const key = obj.allConfigurations.find(
      (config) => config.name === 'Handle Material',
    ).value;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});

  // check that the variants in the groupedMaterials are not all disabled
  Object.entries(groupedMaterials).forEach(([key, value]) => {
    // Check all edges for any enabled configuration
    const enabledVariants = value.filter((product) =>
      product.variants?.edges?.some((edge) => edge.node?.configurationEnabled),
    );

    if (groupedMaterials[key].length <= 1) {
      if (enabledVariants.length === 0) {
        delete groupedMaterials[key];
        return;
      }
      return;
    }

    if (enabledVariants.length <= 1) {
      delete groupedMaterials[key];
    }
  });

  const order = colorAndMaterialOrder || [
    'WOOD',
    'GRIVORY',
    'G10',
    'RICHLITE',
    'CARBON FIBER',
    'TITANIUM',
  ]; // order based on metaobject

  const reorderedMaterials = order.reduce((acc, key) => {
    if (key in groupedMaterials) {
      acc[key] = groupedMaterials[key];
    }
    return acc;
  }, {});

  const groupOrder = Object.keys(reorderedMaterials);

  const slides = (products, key) => {
    let skipProduct = false;

    return products.map((product) =>
      product.variants.edges.map((variant, variantIndex) => {
        const configEnabled = variant.node.configurationEnabled;
        const hasLine =
          variantIndex === 0 && key !== groupOrder[0] && category === 'All';
        const hasBladeSide = Object.keys(variant.node.configuration).some(
          (key) => key.includes(bladeSide),
        );
        const hasBladeSideColor = product.allConfigurations.some(
          (config) =>
            // an exact match for 'Handle Color' means there is no particular blade side color
            config.name !== 'Handle Color' && config.name.includes(`Color`),
        );
        const variantColor = variant.node.image.alt
          ? variant.node.image.alt.toLowerCase()
          : variant.node.title.toLowerCase();

        if (
          (hasBladeSide && hasBladeSideColor) ||
          (!hasBladeSide && !hasBladeSideColor)
        ) {
          if (!hasBladeSideColor && skipProduct) {
            skipProduct = false;
            return null;
          }
          if (!hasBladeSideColor && products.length >= 2) {
            skipProduct = true;
          }

          if (configEnabled) {
            const slideClassName = `flex h-[50px] mx-[6.5px] ${
              hasLine
                ? 'min-w-[75px] max-w-[75px]'
                : 'min-w-[60px] max-w-[60px]'
            }`;
            const borderClassName = `rounded-md relative ${
              selectedVariant === variant
                ? 'border-white border-2 button-active'
                : 'border-transparent border-2 border-opacity-30'
            }`;

            return (
              <SwiperSlide
                key={`${variant.node.title}-${product.customizerTitle}`}
                className={slideClassName}
              >
                {hasLine && (
                  <div className="h-[50px] w-[1px] bg-bg-1 mr-[13px]" />
                )}
                <ClickSelection
                  element="button"
                  variant={variant}
                  className={borderClassName}
                >
                  <Image
                    {...variant.node.image}
                    alt={variantColor}
                    title={variantColor}
                  />
                  <p
                    className={clsx(
                      'absolute left-1/2 -translate-x-1/2 whitespace-nowrap mt-2 capitalize font-m-12',
                      {
                        invisible: !(selectedVariant === variant),
                        'text-white': selectedVariant === variant && darkMode,
                        'text-black': selectedVariant === variant && !darkMode,
                      },
                    )}
                  >
                    {variantColor}
                  </p>
                </ClickSelection>
              </SwiperSlide>
            );
          }
        }

        return null;
      }),
    );
  };

  const setSlider = () => {
    const sliderSlides = Object.entries(reorderedMaterials)
      .map(([key, value]) => {
        if (category === 'All' || category === key) {
          return slides(value, key);
        }
        return null;
      })
      .flat()
      .flat()
      .filter((slide) => slide !== null && slide !== undefined);

    if (sliderSlides.length <= 5) {
      return <div className="flex">{sliderSlides}</div>;
    }

    return (
      <Swiper
        slidesPerView={9}
        slidesOffsetAfter={320}
        breakpoints={{
          1024: {
            slidesPerView: 18,
          },
        }}
      >
        {sliderSlides}
      </Swiper>
    );
  };

  useEffect(() => {
    if (buttonContainerRef && bladeColorDuplicateSide) {
      if (buttonContainerRef?.current?.querySelector('.button-active')) {
        buttonContainerRef.current.querySelector('.button-active')?.click();
      } else {
        buttonContainerRef.current.querySelector('button')?.click();
      }
    }
  }, [bladeColorDuplicateSide]);

  const getPrice = (products) => {
    const productAmount =
      parseFloat(
        products.find(
          (product) =>
            parseFloat(product.variants.edges[0].node.priceV2.amount) !== 0,
        )?.variants.edges[0].node.priceV2.amount,
      ) || 0;
    const selectedVariantAmount =
      parseFloat(selectedVariant?.node.priceV2.amount) || 0;

    const shareSameProductHandle = products.some(
      (product) => product.handle === selectedVariant?.node.product?.handle,
    );

    if (shareSameProductHandle && productAmount !== selectedVariantAmount) {
      return formatMoney({ amount: selectedVariantAmount });
    }

    return formatMoney({ amount: productAmount });
  };

  return (
    <div className="relative mb-[12px]">
      {modalName === 'Compare Materials' ? (
        <CompareMaterialsModal materials={reorderedMaterials} />
      ) : (
        <>
          <div className="mb-[9px] laptop:mb-[28px] laptop:mt-[20px]">
            <Tab.Group>
              <Tab.List className="flex-wrap justify-center m-auto laptop:flex laptop:w-fit">
                <Swiper
                  className="laptop:!overflow-visible"
                  slidesPerView={3}
                  breakpoints={{
                    1024: {
                      allowTouchMove: false,
                    },
                  }}
                >
                  <SwiperSlide className="!w-auto">
                    <Tab
                      onClick={() => setCategory('All')}
                      className={clsx(
                        'font-m-12 uppercase px-[18px] py-[6px]',
                        {
                          'text-white':
                            (!darkMode && variantBackgroundColor?.isDark) ||
                            darkMode,
                          'text-black': !variantBackgroundColor?.isDark,
                          'bg-bg-1 bg-opacity-20 text-white':
                            category === 'All',
                          'border-opacity-20 border-bg-1': darkMode,
                          'text-black bg-bg-1': !darkMode && category !== 'All',
                        },
                      )}
                      style={{
                        backgroundColor:
                          backgroundColor?.presetLightMode && category === 'All'
                            ? variantBackgroundColor?.hex
                            : '',
                      }}
                    >
                      All
                    </Tab>
                  </SwiperSlide>
                  {Object.entries(reorderedMaterials).map(
                    ([material, products]) => (
                      <SwiperSlide
                        className="!w-auto"
                        key={`${material}-button`}
                      >
                        <Tab
                          onClick={() => setCategory(material)}
                          className={clsx(
                            'font-m-12 uppercase px-[18px] py-[6px]',
                            {
                              'text-white': darkMode,
                              'bg-bg-1 bg-opacity-20 text-white':
                                darkMode && category === material,
                              'border-opacity-20 border-bg-1': darkMode,
                              'text-black bg-bg-1':
                                !darkMode && category !== material,
                              'text-white bg-bg-1':
                                !darkMode && category === material,
                            },
                          )}
                          style={{
                            backgroundColor:
                              backgroundColor?.presetLightMode &&
                              category === material
                                ? variantBackgroundColor?.hex
                                : '',
                          }}
                        >
                          <span>{material}</span>
                          <span className="ml-1 text-accent-1">
                            {getPrice(products)}
                          </span>
                        </Tab>
                      </SwiperSlide>
                    ),
                  )}
                  <SwiperSlide className="!w-auto">
                    <Tab
                      onClick={() => openModal('Compare Materials')}
                      className={clsx(
                        'font-m-12 uppercase border-none px-[18px] py-[6px]',
                        {
                          'text-white': darkMode,
                          'text-black bg-bg-1': !darkMode,
                        },
                      )}
                    >
                      Compare Materials
                    </Tab>
                  </SwiperSlide>
                  <div
                    className={clsx(
                      'w-full h-[1px] bg-opacity-20 laptop:mt-[-0.5px]',
                      darkMode ? 'bg-bg-1' : 'bg-bg-2',
                    )}
                  />
                </Swiper>
              </Tab.List>
            </Tab.Group>
          </div>
          <div
            onClick={() => setBladeColorDuplicateSide(!bladeColorDuplicateSide)}
            className="laptop:absolute top-0 flex items-center cursor-pointer px-[18px] laptop:px-[30px] py-[12px] laptop:py-0 laptop:pb-[30px] laptop:justify-end"
          >
            <div
              className={clsx(
                'h-5 w-5 rounded-sm flex items-center justify-center mr-3 border',
                darkMode ? 'border-bg-1' : 'border-bg-2',
              )}
            >
              {bladeColorDuplicateSide && (
                <ThreekitIcons
                  type="checkmark"
                  className={clsx(darkMode ? 'text-white' : 'text-black')}
                />
              )}
            </div>
            <span
              className={clsx(
                'font-m-12',
                darkMode ? 'text-white' : 'text-black',
              )}
            >
              Apply to front and back
            </span>
          </div>
          <div
            className="flex justify-center [&_.swiper]:h-[75px] [&_.swiper-wrapper]:h-auto [&_>.flex]:h-[75px] [&_.flex_.swiper-slide]:h-fit"
            ref={buttonContainerRef}
          >
            {setSlider()}
          </div>
        </>
      )}
    </div>
  );
}

export default HandleDrawerColors;
